import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Button,
   FormControl,
   FormLabel,
   Grid,
   GridItem,
   useColorMode,
   Flex,
   Switch,
   Box,
   Input,
   FormErrorMessage,
   Text,
   Badge,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { getReportStockDetail } from '../../../../../redux/actions/GlobalIMEI';
import moment from 'moment';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
import { useUser } from 'contexts/UserContext';
import { toast } from 'react-toastify';
import { hasPurchase } from 'utils/util';

const FastInput = memo(({ inputRef, value, onChange, onKeyDown, onKeyPress, autoFocus, label, error, ...props }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [localValue, setLocalValue] = useState(value);

   useEffect(() => {
      setLocalValue(value);
   }, [value]);

   const handleChange = useCallback(
      (e) => {
         const newValue = e.target.value;
         setLocalValue(newValue);
         onChange(e);
      },
      [onChange],
   );

   return (
      <FormControl isInvalid={!!error}>
         <FormLabel fontSize="14px" fontWeight="600" color={isDark ? '#e2e8f0' : '#2d3748'} mb={2}>
            {label}
         </FormLabel>
         <Input
            ref={inputRef}
            value={localValue}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            onKeyPress={onKeyPress}
            autoFocus={autoFocus}
            bg={isDark ? '#2d3748' : 'white'}
            border={isDark ? '2px solid #4a5568' : '2px solid #e2e8f0'}
            color={isDark ? '#e2e8f0' : '#2d3748'}
            borderRadius="12px"
            px={4}
            py={2}
            fontSize="14px"
            _focus={{
               borderColor: '#667eea',
               boxShadow: '0 0 0 1px #667eea',
               outline: 'none',
            }}
            _hover={{
               borderColor: isDark ? '#667eea' : '#cbd5e0',
            }}
            transition="all 0.2s"
            {...props}
         />
         {error && <FormErrorMessage fontSize="12px">{error}</FormErrorMessage>}
      </FormControl>
   );
});

FastInput.displayName = 'FastInput';

const RowComponent = memo(
   ({
      initialRow,
      index,
      totalRows,
      isDark,
      onSerialChange,
      onSerial2Change,
      onNoteChange,
      onCheckboxChange,
      onWarehouseChange,
      handleKeyDown,
      handleKeyDownSerial,
      error,
      selectcompany,
      lineitemview,
      warehouseList,
      textColor,
      removeRow,
      addRow,
      serialNoRef,
      serialNo2Ref,
      note1Ref,
      warehouseRef,
      plusRef,
      isValidSerial,
   }) => {
      const [row, setRow] = useState(initialRow);

      useEffect(() => {
         setRow(initialRow);
      }, [initialRow]);

      const handleSerialChangeLocal = useCallback(
         (e) => {
            const value = e.target.value;
            setRow((prev) => ({ ...prev, serialNo: value }));
            onSerialChange(e, index);
         },
         [index, onSerialChange],
      );

      const handleSerial2ChangeLocal = useCallback(
         (e) => {
            const value = e.target.value;
            setRow((prev) => ({ ...prev, serialNo2: value }));
            onSerial2Change(e, index);
         },
         [index, onSerial2Change],
      );

      const handleNoteChangeLocal = useCallback(
         (e) => {
            const value = e.target.value;
            setRow((prev) => ({ ...prev, note1: value }));
            onNoteChange(e, index);
         },
         [index, onNoteChange],
      );

      const handleCheckboxChangeLocal = useCallback(
         (e) => {
            const { name, checked } = e.target;
            setRow((prev) => ({ ...prev, [name]: checked }));
            onCheckboxChange(e, index);
         },
         [index, onCheckboxChange],
      );

      const handleWarehouseChangeLocal = useCallback(
         (value) => {
            setRow((prev) => ({ ...prev, warehouseId: value }));
            onWarehouseChange(value, index);
         },
         [index, onWarehouseChange],
      );

      return (
         <Box
            border={isDark ? '2px solid #4a5568' : '2px solid #e2e8f0'}
            bg={isDark ? '#2d3748' : '#f7fafc'}
            p="24px"
            borderRadius="20px"
            mb={6}
            width="100%"
            boxShadow={isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.4)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}
            transition="all 0.3s ease-in-out"
            position="relative"
            _hover={{
               transform: 'translateY(-4px)',
               boxShadow: isDark
                  ? '0 10px 15px -3px rgba(102, 126, 234, 0.3)'
                  : '0 10px 15px -3px rgba(102, 126, 234, 0.2)',
            }}>
            <Badge
               position="absolute"
               top="-12px"
               left="24px"
               bg={
                  isValidSerial
                     ? 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
                     : row.serialNo?.trim() === ''
                        ? isDark
                           ? 'linear-gradient(135deg, #718096 0%, #4a5568 100%)'
                           : 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                        : 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)'
               }
               color="white"
               fontSize="sm"
               fontWeight="800"
               px={3}
               py={1}
               borderRadius="8px"
               zIndex={1}>
               Row {index + 1}
               {isValidSerial && (
                  <Text as="span" ml={1} fontSize="xs" opacity={0.9}>
                     ✓
                  </Text>
               )}
            </Badge>

            <Grid display="flex" gap={5} width="100%">
               <GridItem gap={3} width="100%">
                  <GridItem gap={3} display="flex" width="100%">
                     <GridItem mb={3} width="50%">
                        <FastInput
                           inputRef={(el) => (serialNoRef.current[index] = el)}
                           name="serialNo"
                           label="Serial NO"
                           value={row.serialNo}
                           onChange={handleSerialChangeLocal}
                           onKeyDown={(e) => {
                              handleKeyDown(e, serialNo2Ref, note1Ref, index);
                              handleKeyDownSerial(e);
                           }}
                           autoFocus={index === totalRows - 1}
                           error={error}
                           disabled={row.processStatus === "ISSUE" || row.processStatus === "SALES"}
                        />
                     </GridItem>
                     <GridItem width="50%">
                        <FastInput
                           inputRef={(el) => (serialNo2Ref.current[index] = el)}
                           name="serialNo2"
                           label="Serial NO 2"
                           value={row.serialNo2}
                           onChange={handleSerial2ChangeLocal}
                           onKeyDown={(e) => handleKeyDown(e, note1Ref, serialNoRef, index)}
                           disabled={row.processStatus === "ISSUE" || row.processStatus === "SALES"}
                        />
                     </GridItem>
                  </GridItem>
                  <GridItem display="flex">
                     <GridItem
                        width="100%"
                        mr={selectcompany?.setting?.warehouseWishStock === true && lineitemview === true ? 3 : 0}>
                        <FastInput
                           inputRef={(el) => (note1Ref.current[index] = el)}
                           name="note1"
                           label="Note"
                           value={row.note1}
                           onChange={handleNoteChangeLocal}
                           onKeyDown={(e) =>
                              handleKeyDown(
                                 e,
                                 selectcompany?.setting?.warehouseWishStock === true && lineitemview === true
                                    ? warehouseRef
                                    : plusRef,
                                 serialNo2Ref,
                                 index,
                              )
                           }
                        />
                     </GridItem>
                     {selectcompany?.setting?.warehouseWishStock === true && lineitemview === true && (
                        <GridItem className="seperator-width mt-7">
                           {renderDynamicDropdown(
                              'warehouseId',
                              'Ware House',
                              warehouseList,
                              {
                                 value: row.warehouseId,
                                 onChange: handleWarehouseChangeLocal,
                                 showCustomNoOptions: true,
                                 openfrom: 'wareHouse',
                                 ref: (el) => {
                                    warehouseRef.current[index] = el;
                                 },
                                 onKeyDown: (e) => {
                                    if (e.key === 'Tab' && e.shiftKey) {
                                       e.preventDefault();
                                       if (note1Ref?.current?.[index]?.focus) {
                                          note1Ref.current[index].focus();
                                       }
                                       return;
                                    }
                                    handleKeyDown(e, plusRef, serialNo2Ref, index);
                                 },
                              },
                              {},
                              textColor,
                              false,
                              'Select Ware House',
                           )}
                        </GridItem>
                     )}
                  </GridItem>

                  <GridItem display="flex" gap={6} mt={4} width="100%">
                     <FormControl
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bg={isDark ? '#1a202c' : 'white'}
                        border={isDark ? '2px solid #667eea' : '2px solid #e2e8f0'}
                        borderRadius="16px"
                        p={4}
                        minW="200px">
                        <FormLabel
                           htmlFor={`Box-${index}`}
                           mb={0}
                           fontSize="14px"
                           fontWeight="600"
                           color={isDark ? '#e2e8f0' : '#2d3748'}>
                           Box
                        </FormLabel>
                        <Switch
                           name="isBox"
                           id={`Box-${index}`}
                           isChecked={row.isBox}
                           onChange={handleCheckboxChangeLocal}
                           colorScheme="blue"
                           size="lg"
                        />
                     </FormControl>

                     <FormControl
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bg={isDark ? '#1a202c' : 'white'}
                        border={isDark ? '2px solid #667eea' : '2px solid #e2e8f0'}
                        borderRadius="16px"
                        p={4}
                        minW="180px">
                        <FormLabel
                           htmlFor={`bill-${index}`}
                           mb={0}
                           fontSize="14px"
                           fontWeight="600"
                           color={isDark ? '#e2e8f0' : '#2d3748'}>
                           Bill
                        </FormLabel>
                        <Switch
                           name="isBill"
                           id={`bill-${index}`}
                           isChecked={row.isBill}
                           onChange={handleCheckboxChangeLocal}
                           colorScheme="green"
                           size="lg"
                        />
                     </FormControl>

                     <FormControl
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bg={isDark ? '#1a202c' : 'white'}
                        border={isDark ? '2px solid #667eea' : '2px solid #e2e8f0'}
                        borderRadius="16px"
                        p={4}
                        minW="160px">
                        <FormLabel
                           htmlFor={`kit-${index}`}
                           mb={0}
                           fontSize="14px"
                           fontWeight="600"
                           color={isDark ? '#e2e8f0' : '#2d3748'}>
                           Kit
                        </FormLabel>
                        <Switch
                           name="isKit"
                           id={`kit-${index}`}
                           isChecked={row.isKit}
                           onChange={handleCheckboxChangeLocal}
                           colorScheme="orange"
                           size="lg"
                        />
                     </FormControl>
                  </GridItem>
               </GridItem>
               <GridItem display="flex" flexDirection="column" alignItems="center" gap={3}>
                  <FormControl
                     display="flex"
                     alignItems="center"
                     justifyContent="center"
                     bg={isDark ? '#1a202c' : 'white'}
                     border={isDark ? '2px solid #667eea' : '2px solid #e2e8f0'}
                     borderRadius="16px"
                     p={4}
                     minW="140px">
                     <FormLabel
                        htmlFor={`idOld-${index}`}
                        style={{ textWrap: 'nowrap' }}
                        mb={0}
                        fontSize="14px"
                        fontWeight="600"
                        color={isDark ? '#e2e8f0' : '#2d3748'}
                        mr={3}>
                        Is Old
                     </FormLabel>
                     <Switch
                        name="isOld"
                        id={`idOld-${index}`}
                        isChecked={row.isOld}
                        onChange={handleCheckboxChangeLocal}
                        colorScheme="purple"
                        size="lg"
                     />
                  </FormControl>
                  <Flex gap={2} width="100%" justifyContent="center">
                     {totalRows > 1 && (
                        <Button
                           bg={
                              isDark
                                 ? 'linear-gradient(135deg, #f56565 0%, #c53030 100%)'
                                 : 'linear-gradient(135deg, #fc8181 0%, #f56565 100%)'
                           }
                           color="white"
                           size="md"
                           width="48px"
                           height="48px"
                           borderRadius="14px"
                           onClick={() => removeRow(index)}
                           fontSize="24px"
                           fontWeight="700"
                           _hover={{
                              transform: 'scale(1.05)',
                              boxShadow: isDark
                                 ? '0 6px 12px -2px rgba(245, 101, 101, 0.4)'
                                 : '0 6px 12px -2px rgba(245, 101, 101, 0.3)',
                           }}
                           _active={{ transform: 'scale(0.95)' }}
                           transition="all 0.2s ease-in-out"
                           _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(245, 101, 101, 0.3)' }}>
                           -
                        </Button>
                     )}
                     {index === totalRows - 1 && (
                        <Button
                           ref={(el) => {
                              if (!plusRef.current) plusRef.current = [];
                              plusRef.current[index] = el;
                           }}
                           bg={
                              isDark
                                 ? 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
                                 : 'linear-gradient(135deg, #68d391 0%, #48bb78 100%)'
                           }
                           color="white"
                           size="md"
                           width="48px"
                           height="48px"
                           borderRadius="14px"
                           onClick={addRow}
                           fontSize="24px"
                           fontWeight="700"
                           _hover={{
                              transform: 'scale(1.05)',
                              boxShadow: isDark
                                 ? '0 6px 12px -2px rgba(72, 187, 120, 0.4)'
                                 : '0 6px 12px -2px rgba(72, 187, 120, 0.3)',
                           }}
                           _active={{ transform: 'scale(0.95)' }}
                           transition="all 0.2s ease-in-out"
                           _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(72, 187, 120, 0.3)' }}>
                           +
                        </Button>
                     )}
                  </Flex>
               </GridItem>
            </Grid>
         </Box>
      );
   },
   (prevProps, nextProps) => {
      return (
         JSON.stringify(prevProps.initialRow) === JSON.stringify(nextProps.initialRow) &&
         prevProps.error === nextProps.error &&
         prevProps.totalRows === nextProps.totalRows &&
         prevProps.isDark === nextProps.isDark &&
         prevProps.isValidSerial === nextProps.isValidSerial
      );
   },
);

RowComponent.displayName = 'RowComponent';

const SerialNumberModal = ({
   isOpen,
   onClose,
   selectedDaybook,
   handleStock,
   itemStocks,
   itemId,
   onCloseComplete,
   setItemStocks,
   removeStock,
   warehouseList,
   lineitemview,
   selectcompany,
   textColor,
   selectdaybookdata,
   lineitem,
   headerId,
}) => {
   const dispatch = useDispatch();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [rows, setRows] = useState([]);
   const [serial, setSerial] = useState([]);
   const [otherSerial, setOtherSerial] = useState([]);
   const [errors, setErrors] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [validSerials, setValidSerials] = useState({});

   const debounceTimeouts = useRef({});
   const { user } = useUser();

   const serialNoRef = useRef([]);
   const serialNo2Ref = useRef([]);
   const note1Ref = useRef([]);
   const warehouseRef = useRef([]);
   const submitRef = useRef(null);
   const plusRef = useRef([]);

   const apiCache = useRef({});
   // ✅ Tracks serials that were rejected so same value always re-triggers a fresh API call
   const rejectedCache = useRef(new Set());
   const validationInProgress = useRef({});

   const counts = useMemo(() => {
      const totalAdded = rows.filter((row) => row?.serialNo?.trim() !== '').length;
      const validCount = Object.values(validSerials).filter(Boolean).length;
      const totalRows = rows.length;
      return { totalAdded, validCount, totalRows };
   }, [rows, validSerials]);

   const isSerialNo = useMemo(() => {
      if (rows.length === 0) return false;
      return rows.some((item) => item.serialNo && item.serialNo?.trim().length >= 10);
   }, [rows]);

   const addRow = useCallback(() => {
      setRows((prevRows) => [
         ...prevRows,
         {
            id: null,
            serialNo: '',
            serialNo2: '',
            barcode: '',
            note1: '',
            isOld: selectedDaybook?.isOld ? true : false,
            warehouseId: selectdaybookdata?.wareHouseId ?? null,
            isBox: false,
            isBill: false,
            isKit: false,
         },
      ]);
   }, [selectedDaybook?.isOld, selectdaybookdata?.wareHouseId]);

   const removeRow = useCallback(
      (index) => {
         setRows((prevRows) => {
            const newRows = prevRows.filter((_, i) => i !== index);
            setItemStocks(newRows);
            removeStock(prevRows[index]);
            return newRows;
         });

         if (debounceTimeouts.current[index]) {
            clearTimeout(debounceTimeouts.current[index]);
            delete debounceTimeouts.current[index];
         }

         delete validationInProgress.current[index];
         delete validSerials[index];
      },
      [setItemStocks, removeStock, validSerials],
   );

   const handleOnClose = useCallback(() => {
      setRows([
         {
            id: null,
            serialNo: '',
            serialNo2: '',
            barcode: '',
            note1: '',
            isOld: selectedDaybook?.isOld,
            warehouseId: null,
            isBox: false,
            isBill: false,
            isKit: false,
         },
      ]);
      setSerial([]);
      setValidSerials({});

      Object.values(debounceTimeouts.current).forEach((timeout) => clearTimeout(timeout));
      debounceTimeouts.current = {};
      validationInProgress.current = {};

      // ✅ Clear all caches on close so next open is fresh
      apiCache.current = {};
      rejectedCache.current.clear();

      onClose();
   }, [selectedDaybook?.isOld, onClose]);

   useEffect(() => {
      const handleEscapeKey = (event) => {
         if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            handleOnClose();
            onClose();
         }
      };
      window.addEventListener('keydown', handleEscapeKey);
      return () => window.removeEventListener('keydown', handleEscapeKey);
   }, [isOpen, onClose, handleOnClose]);

   const handleCancel = useCallback(() => {
      handleOnClose();
      onClose();
   }, [handleOnClose, onClose]);

   const handleSave = useCallback(async () => {
      if (isSaving) return;

      const invalidSerials = [];
      rows.forEach((row, index) => {
         const serialNo = row.serialNo?.trim();
         if (serialNo !== '') {
            if (serialNo.length < 10) {
               invalidSerials.push(`Row ${index + 1}: Serial number must be at least 10 characters`);
            }
            if (!validSerials[index]) {
               invalidSerials.push(`Row ${index + 1}: Serial number validation failed`);
            }
         }
      });

      if (invalidSerials.length > 0) {
         toast.error(
            <Box>
               <Text fontWeight="bold">Cannot save due to validation errors:</Text>
               {invalidSerials.map((error, i) => (
                  <Text key={i} fontSize="sm">
                     • {error}
                  </Text>
               ))}
            </Box>,
            { autoClose: 5000 },
         );
         return;
      }

      const filteredRows = rows.filter((row) => row.serialNo?.trim() !== '');

      if (filteredRows.length === 0) {
         toast.error('Please add at least one valid serial number');
         return;
      }

      const allValidated = filteredRows.every((_, index) => validSerials[index]);
      if (!allValidated) {
         toast.error('Please validate all serial numbers before saving');
         return;
      }

      setIsSaving(true);

      try {
         await handleStock(filteredRows);
         toast.success(`Successfully saved ${filteredRows.length} serial number(s)`, { autoClose: 3000 });
         handleOnClose();
      } catch (error) {
         console.error('Error saving serial numbers:', error);
         toast.error('Failed to save serial numbers');
      } finally {
         setIsSaving(false);
      }
   }, [isSaving, rows, handleStock, handleOnClose, validSerials]);

   useEffect(() => {
      if (itemStocks?.length > 0) {
         setRows(itemStocks);
         const initialValidSerials = {};
         itemStocks.forEach((row, index) => {
            if (row.serialNo && row.serialNo?.trim().length >= 10) {
               initialValidSerials[index] = true;
            }
         });
         setValidSerials(initialValidSerials);
      }
   }, [itemStocks]);

   useEffect(() => {
      if (lineitem?.length > 0) {
         const allserial = lineitem?.map((li) => li?.stock?.map((stock) => stock?.serialNo)).flat();
         setOtherSerial(allserial);
      }
   }, [isOpen, lineitem]);

   useEffect(() => {
      if (selectedDaybook) {
         setRows([
            {
               id: null,
               serialNo: '',
               serialNo2: '',
               barcode: '',
               note1: '',
               isOld: selectedDaybook?.isOld,
               warehouseId: null,
               isBox: false,
               isBill: false,
               isKit: false,
            },
         ]);
         setValidSerials({});
      }
   }, [selectedDaybook]);

   useEffect(() => {
      if (selectcompany?.setting?.warehouseWishStock === true) {
         if (lineitemview === true) {
            setRows((prevRows) =>
               Array.isArray(prevRows)
                  ? prevRows.map((row) => ({
                     ...row,
                     warehouseId:
                        row.warehouseId === null
                           ? selectdaybookdata?.wareHouseId
                              ? selectdaybookdata?.wareHouseId
                              : selectcompany?.setting?.warehouseId
                                 ? selectcompany?.setting?.warehouseId
                                 : 1
                           : row.warehouseId,
                  }))
                  : [],
            );
         }
      }
   }, [isOpen, selectcompany, selectdaybookdata, lineitemview]);

   // ✅ Removes a serial from both caches so next entry always re-runs fresh API + duplicate checks
   const clearSerialFromCache = useCallback((value) => {
      const trimmed = value?.trim();
      if (!trimmed) return;
      delete apiCache.current[trimmed];
      rejectedCache.current.delete(trimmed);
   }, []);

   const handleSearch = useCallback(
      async (value, idx) => {
         if (validationInProgress.current[idx]) return;

         validationInProgress.current[idx] = true;
         const trimmedValue = value?.trim();

         // ✅ Rejects a serial: clears field, marks invalid, and flags for re-validation next time
         const rejectSerial = () => {
            rejectedCache.current.add(trimmedValue);
            setRows((prevRows) => {
               const newRows = [...prevRows];
               if (newRows[idx]) {
                  newRows[idx] = { ...newRows[idx], serialNo: '' };
               }
               return newRows;
            });
            setValidSerials((prev) => ({ ...prev, [idx]: false }));
         };

         try {
            // ✅ Skip cache entirely if this serial was previously rejected
            const isRejected = rejectedCache.current.has(trimmedValue);
            const hasCached = !!apiCache.current[trimmedValue];

            if (hasCached && !isRejected) {
               const cachedResult = apiCache.current[trimmedValue];

               if (
                  cachedResult.code === 1 &&
                  cachedResult.data?.length > 0 &&
                  !hasPurchase(cachedResult?.data, Number(headerId || 0))
               ) {
                  rejectSerial();
                  return cachedResult;
               }
               if (cachedResult.code === 2) {
                  rejectSerial();
                  return cachedResult;
               }

               setValidSerials((prev) => ({ ...prev, [idx]: true }));
               return cachedResult;
            }

            // Fresh API call (either no cache, or previously rejected)
            setIsLoading(true);
            const params = {
               companyIds: user?.loginhistory?.companyId,
               fromDate: '2024-01-01',
               toDate: moment().add(1, 'days').format('YYYY-MM-DD'),
               stockStatus: 2,
               serialNo: trimmedValue,
               headerId: headerId,
            };

            const res = await dispatch(getReportStockDetail(params));

            // ✅ Store fresh result and clear rejected flag before evaluating outcome
            apiCache.current[trimmedValue] = res;
            rejectedCache.current.delete(trimmedValue);

            if (res.code === 1 && res.data?.length > 0 && !hasPurchase(res?.data, Number(headerId || 0))) {
               rejectSerial();
            } else if (res.code === 2) {
               rejectSerial();
            } else {
               setValidSerials((prev) => ({ ...prev, [idx]: true }));
            }

            return res;
         } finally {
            setIsLoading(false);
            validationInProgress.current[idx] = false;
         }
      },
      [dispatch, headerId, user?.loginhistory?.companyId],
   );

   const validateSerial = useCallback(
      (value, index) => {
         const trimmedValue = value?.trim();

         const isDuplicateInCurrent = rows.some((row, i) => i !== index && row.serialNo?.trim() === trimmedValue);
         if (isDuplicateInCurrent) {
            toast.error(`Duplicate serial number: ${trimmedValue}`);
            // ✅ Remove from both caches so next entry of same value runs full validation again
            clearSerialFromCache(trimmedValue);
            setRows((prev) => {
               const updated = [...prev];
               if (updated[index]) {
                  updated[index] = { ...updated[index], serialNo: '' };
               }
               return updated;
            });
            setValidSerials((prev) => ({ ...prev, [index]: false }));
            return false;
         }

         const isDuplicateInOther = otherSerial.includes(trimmedValue);
         if (isDuplicateInOther) {
            toast.error(`Duplicate serial number: ${trimmedValue}`);
            // ✅ Remove from both caches so next entry of same value runs full validation again
            clearSerialFromCache(trimmedValue);
            setRows((prev) => {
               const updated = [...prev];
               if (updated[index]) {
                  updated[index] = { ...updated[index], serialNo: '' };
               }
               return updated;
            });
            setValidSerials((prev) => ({ ...prev, [index]: false }));
            return false;
         }

         return true;
      },
      [rows, otherSerial, clearSerialFromCache],
   );

   const handleKeyDown = useCallback(
      (e, nextRef, preRef, index) => {
         if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            const serialNumber = e.target.value?.trim();
            const name = e.target.name;

            if (rows.length === 1 && serialNumber === '' && name === 'serialNo' && index === 0) {
               toast.warning('Please add a serial number first');
               e.target.focus();
               return;
            }

            if (name === 'serialNo' && serialNumber === '') {
               if (index === 0 && rows.length === 1) {
                  if (nextRef?.current?.[index]?.focus) {
                     nextRef.current[index].focus();
                  }
                  return;
               }

               setRows((prevRows) => {
                  const newRows = prevRows.filter((_, i) => i !== index);
                  setItemStocks(newRows);
                  return newRows;
               });
               setTimeout(() => {
                  if (submitRef.current) submitRef.current.focus();
               }, 0);
               return;
            }

            if (name === 'note1') {
               if (nextRef === plusRef) {
                  if (nextRef?.current?.[index]?.focus) nextRef.current[index].focus();
                  return;
               }
               if (nextRef === warehouseRef) {
                  if (nextRef?.current?.[index]?.focus) nextRef.current[index].focus();
                  return;
               }
               if (nextRef?.current) {
                  nextRef.current.focus();
                  return;
               }
            }

            if (nextRef?.current?.[index]?.focus) {
               nextRef.current[index].focus();
               return;
            }
         }

         if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            const name = e.target.name;
            if (name === 'serialNo' && preRef?.current?.[index - 1]?.focus) {
               preRef.current[index - 1].focus();
               return;
            }
            if (preRef?.current?.[index]?.focus) {
               preRef.current[index].focus();
               return;
            }
         }
      },
      [rows, setItemStocks],
   );

   const handleKeyDownSerial = useCallback((e) => {
      if (e.key === 'Enter' || e.key === 'Tab') return;
      const regex = /^[a-zA-Z0-9]$/;
   }, []);

   const updateRow = useCallback((index, field, value) => {
      setRows((prevRows) => {
         const newRows = [...prevRows];
         if (newRows[index]) {
            newRows[index][field] = value;
         }
         return newRows;
      });
   }, []);

   const createHandlers = useCallback(
      (index) => {
         return {
            handleSerialChange: (e) => {
               const value = e.target.value;

               updateRow(index, 'serialNo', value);

               if (value?.trim().length < 10) {
                  setValidSerials((prev) => ({ ...prev, [index]: false }));
               }

               if (debounceTimeouts.current[index]) {
                  clearTimeout(debounceTimeouts.current[index]);
               }

               if (value?.trim().length >= 10) {
                  debounceTimeouts.current[index] = setTimeout(async () => {
                     if (!validateSerial(value, index)) return;
                     await handleSearch(value, index);
                  }, 300);
               }
            },

            handleSerial2Change: (e) => {
               updateRow(index, 'serialNo2', e.target.value);
            },

            handleNoteChange: (e) => {
               updateRow(index, 'note1', e.target.value);
            },

            handleCheckboxChange: (e) => {
               const { name, checked } = e.target;
               updateRow(index, name, checked);
            },

            handleWarehouseChange: (value) => {
               updateRow(index, 'warehouseId', value);
            },
         };
      },
      [updateRow, validateSerial, handleSearch],
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={handleCancel}
         size="6xl"
         isCentered
         onCloseComplete={onCloseComplete}
         closeOnEsc={true}>
         <ModalOverlay bg="blackAlpha.600" />
         <ModalContent
            bg={isDark ? '#1a202c' : '#ffffff'}
            borderRadius="24px"
            p={0}
            boxShadow={isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.8)' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)'}
            border={isDark ? '1px solid #2d3748' : '1px solid #e2e8f0'}
            maxH="90vh"
            overflow="hidden">
            <ModalHeader
               fontSize="24px"
               fontWeight="700"
               bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
               color="white"
               borderTopRadius="24px"
               py={6}
               px={8}
               position="relative">
               <Flex justifyContent="space-between" alignItems="center">
                  <Box>Add Serial Number</Box>
               </Flex>
               <ModalCloseButton color="white" top={6} right={6} _hover={{ bg: 'whiteAlpha.300' }} borderRadius="full" />
            </ModalHeader>
            <ModalBody
               p={8}
               overflowY="auto"
               maxH="calc(90vh - 220px)"
               css={{
                  '&::-webkit-scrollbar': { width: '12px' },
                  '&::-webkit-scrollbar-track': {
                     background: isDark ? '#1a202c' : '#f1f1f1',
                     borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                     background: isDark ? '#667eea' : '#cbd5e0',
                     borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                     background: isDark ? '#764ba2' : '#a0aec0',
                  },
               }}>
               <Box width="100%">
                  {rows?.map((row, index) => {
                     const handlers = createHandlers(index);
                     return (
                        <RowComponent
                           key={`row-${index}-${row.id || 'new'}`}
                           initialRow={row}
                           index={index}
                           totalRows={rows.length}
                           isDark={isDark}
                           onSerialChange={handlers.handleSerialChange}
                           onSerial2Change={handlers.handleSerial2Change}
                           onNoteChange={handlers.handleNoteChange}
                           onCheckboxChange={handlers.handleCheckboxChange}
                           onWarehouseChange={handlers.handleWarehouseChange}
                           handleKeyDown={handleKeyDown}
                           handleKeyDownSerial={handleKeyDownSerial}
                           error={errors[`serialNo_${index}`]}
                           selectcompany={selectcompany}
                           lineitemview={lineitemview}
                           warehouseList={warehouseList}
                           textColor={textColor}
                           removeRow={removeRow}
                           addRow={addRow}
                           serialNoRef={serialNoRef}
                           serialNo2Ref={serialNo2Ref}
                           note1Ref={note1Ref}
                           warehouseRef={warehouseRef}
                           plusRef={plusRef}
                           isValidSerial={validSerials[index]}
                        />
                     );
                  })}
               </Box>
            </ModalBody>
            <ModalFooter
               justifyContent="space-between"
               alignItems="center"
               bg={isDark ? '#1a202c' : '#f7fafc'}
               borderBottomRadius="24px"
               py={6}
               px={8}
               borderTop={isDark ? '2px solid #4a5568' : '2px solid #e2e8f0'}>
               <Flex direction="column">
                  <Text fontSize="sm" color={isDark ? '#a0aec0' : '#718096'} mb={1}>
                     Current Status
                  </Text>
                  <Flex gap={4} alignItems="center">
                     <Text fontSize="md" fontWeight="600" color={isDark ? '#e2e8f0' : '#2d3748'}>
                        {counts.totalAdded} serial{counts.totalAdded !== 1 ? 's' : ''}
                        {counts.validCount > 0 && ` (${counts.validCount} validated)`}
                     </Text>
                     {counts.totalAdded > 0 && counts.validCount === 0 && (
                        <Text fontSize="sm" color={isDark ? '#ed8936' : '#dd6b20'} fontWeight="500">
                           ⚠ Need validation
                        </Text>
                     )}
                  </Flex>
               </Flex>

               <Flex gap={4}>
                  <Button
                     bg={
                        isDark
                           ? 'linear-gradient(135deg, #f56565 0%, #c53030 100%)'
                           : 'linear-gradient(135deg, #fc8181 0%, #f56565 100%)'
                     }
                     color="white"
                     onClick={handleCancel}
                     size="lg"
                     fontWeight="700"
                     fontSize="16px"
                     px={10}
                     borderRadius="14px"
                     _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: isDark
                           ? '0 10px 20px -5px rgba(245, 101, 101, 0.4)'
                           : '0 10px 20px -5px rgba(245, 101, 101, 0.3)',
                     }}
                     _active={{ transform: 'translateY(0)' }}
                     _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(245, 101, 101, 0.3)' }}
                     transition="all 0.2s ease-in-out">
                     Cancel
                  </Button>
                  <Button
                     ref={submitRef}
                     bg={
                        counts.totalAdded > 0
                           ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                           : isDark
                              ? 'linear-gradient(135deg, #718096 0%, #4a5568 100%)'
                              : 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                     }
                     color="white"
                     size="lg"
                     fontWeight="700"
                     fontSize="16px"
                     px={10}
                     borderRadius="14px"
                     onClick={handleSave}
                     isDisabled={
                        isLoading || counts.totalAdded === 0 || isSaving || counts.validCount !== counts.totalAdded
                     }
                     isLoading={isLoading || isSaving}
                     _hover={
                        counts.totalAdded > 0
                           ? {
                              transform: 'translateY(-2px)',
                              boxShadow: isDark
                                 ? '0 10px 20px -5px rgba(102, 126, 234, 0.4)'
                                 : '0 10px 20px -5px rgba(102, 126, 234, 0.3)',
                           }
                           : {}
                     }
                     _active={{ transform: 'translateY(0)' }}
                     _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.3)' }}
                     transition="all 0.2s ease-in-out">
                     {isSaving ? 'Saving...' : `Save (${counts.totalAdded})`}
                  </Button>
               </Flex>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default SerialNumberModal;